/**
 * Presentation contract for the consent Audit log drawer.
 *
 * This module is the only seam between `getLeadAuditLog` and the two components
 * that render it. It holds the server's `category` tokens, the chip row, and the
 * icon/colour vocabulary; `mapAuditLogEntry.js` turns a raw row into the shape
 * `AuditLogItem` draws.
 *
 * Copy, ordering, icon glyphs and colours come from the Figma frame "Drawer"
 * (file NDVwkatBby4TFCN5oag0D1, node 27023-48954).
 */

/**
 * Server-side `category` argument values. Sent verbatim as the query variable —
 * these are the API's tokens, not display labels.
 */
export const AUDIT_LOG_CATEGORY = {
  SMS_CONSENT: "SMS_CONSENT",
  EMAIL_CONSENT: "EMAIL_CONSENT",
  OTTO_NUDGE: "OTTO_NUDGE",
  AUTO_RESPONDER: "AUTO_RESPONDER",
  OTTO_CAMPAIGN: "OTTO_CAMPAIGN",
};

/** Glyph to render, resolved to a component by AuditLogItem. */
export const AUDIT_LOG_ICON = {
  CHAT: "CHAT",
  MAIL: "MAIL",
  OTTO: "OTTO",
};

/**
 * Icon colours, as design tokens off the Figma inspect panel.
 * green Colors/Base/Green/6 · red Colors/Base/Red/5 ·
 * blue Colors/Brand/Primary/colorPrimary · amber Colors/Base/Orange/6 ·
 * grey Colors/Neutral/Icon/colorIcon
 */
export const AUDIT_LOG_ICON_COLOR = {
  GREEN: "#52C41A",
  RED: "#FF4D4F",
  BLUE: "#1677FF",
  AMBER: "#FA8C16",
  GREY: "rgba(0, 0, 0, 0.45)",
};

/**
 * Chip row, in design order — `langKey` resolves against `en.json`. The leading
 * "All" chip is ChipCheckboxGroup's own, so it is absent here; "All" is an empty
 * selection, which means the `category` variable is omitted from the query.
 */
export const AUDIT_LOG_FILTERS = [
  { langKey: "auditLogFilterSmsConsent", value: AUDIT_LOG_CATEGORY.SMS_CONSENT },
  { langKey: "auditLogFilterEmailConsent", value: AUDIT_LOG_CATEGORY.EMAIL_CONSENT },
  { langKey: "auditLogFilterOttoNudges", value: AUDIT_LOG_CATEGORY.OTTO_NUDGE },
  { langKey: "auditLogFilterAutoResponder", value: AUDIT_LOG_CATEGORY.AUTO_RESPONDER },
  { langKey: "auditLogFilterOttoCampaign", value: AUDIT_LOG_CATEGORY.OTTO_CAMPAIGN },
];

/** Rows per request. The drawer pages rather than infinite-scrolls. */
export const AUDIT_LOG_PAGE_SIZE = 20;

/**
 * Semantic tone of a resulting value, which drives the row's icon colour.
 * Derived from the new value, so a row reads as on/off at a glance.
 */
export const AUDIT_LOG_TONE = {
  POSITIVE: "POSITIVE", // opted in, enrolled, turned on
  NEGATIVE: "NEGATIVE", // opted out, declined
  NEUTRAL: "NEUTRAL", // turned off, disenrolled
  PENDING: "PENDING", // consent requested, implied
  WARNING: "WARNING", // paused
};

export const AUDIT_LOG_TONE_COLOR = {
  [AUDIT_LOG_TONE.POSITIVE]: AUDIT_LOG_ICON_COLOR.GREEN,
  [AUDIT_LOG_TONE.NEGATIVE]: AUDIT_LOG_ICON_COLOR.RED,
  [AUDIT_LOG_TONE.NEUTRAL]: AUDIT_LOG_ICON_COLOR.GREY,
  [AUDIT_LOG_TONE.PENDING]: AUDIT_LOG_ICON_COLOR.BLUE,
  [AUDIT_LOG_TONE.WARNING]: AUDIT_LOG_ICON_COLOR.AMBER,
};

/**
 * Per-category rendering rules.
 *
 * `fields` are the audit row's `changedFields` entries that belong to this
 * category — used to attribute a row when the drawer is on "All" and the server
 * therefore does not narrow by category. They are the lead columns behind the
 * Consents panel (`textConsentStatus`, `emailConsent`, `campaignAutoResponse`);
 * both snake_case and camelCase are accepted, so the lookup is normalised.
 *
 * `values` maps a stored value to `{ label, tone }`, rendered as
 * "{noun} {verb} {label}". A value whose sentence does not fit that frame gives
 * a full `title` instead. Anything not listed falls back to a humanised form of
 * the raw value with a neutral tone, so an unrecognised state still renders a
 * readable row instead of a blank one.
 */
export const AUDIT_LOG_CATEGORY_CONFIG = {
  [AUDIT_LOG_CATEGORY.SMS_CONSENT]: {
    icon: AUDIT_LOG_ICON.CHAT,
    noun: "SMS consent",
    verb: "set to",
    fields: ["text_consent_status", "sms_consent", "consent_status"],
    values: {
      ACCEPTED: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      DECLINED: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
      // Not "SMS consent set to Pending": a pending status means the request
      // went out and the lead is on implied consent until they answer.
      PENDING: {
        title: "SMS consent request sent — implied consent",
        tone: AUDIT_LOG_TONE.PENDING,
      },
      TRUE: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
    },
  },
  [AUDIT_LOG_CATEGORY.EMAIL_CONSENT]: {
    icon: AUDIT_LOG_ICON.MAIL,
    noun: "Email",
    verb: "set to",
    fields: ["email_consent"],
    values: {
      TRUE: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
      // email_consent is a nullable boolean: null = never recorded, which the
      // Consents panel reads as implied consent (EAI-1666). Keep it distinct
      // from false — collapsing the two would misreport an opt-out.
      NULL: { label: "Implied consent", tone: AUDIT_LOG_TONE.PENDING },
    },
  },
  [AUDIT_LOG_CATEGORY.OTTO_NUDGE]: {
    icon: AUDIT_LOG_ICON.OTTO,
    noun: "Otto nudges",
    verb: "turned",
    fields: ["otto_nudge", "otto_nudge_enabled", "nudge_enabled", "is_nudge_enabled"],
    values: {
      TRUE: { label: "on", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "off", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
  [AUDIT_LOG_CATEGORY.AUTO_RESPONDER]: {
    icon: AUDIT_LOG_ICON.OTTO,
    noun: "Otto response",
    verb: "changed to",
    fields: ["campaign_auto_response", "auto_response", "auto_responder_status"],
    values: {
      ENROLLED: { label: "Enrolled", tone: AUDIT_LOG_TONE.POSITIVE },
      PAUSED: { label: "Paused", tone: AUDIT_LOG_TONE.WARNING },
      DISENROLLED: { label: "Disenrolled", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
  [AUDIT_LOG_CATEGORY.OTTO_CAMPAIGN]: {
    icon: AUDIT_LOG_ICON.OTTO,
    noun: "Otto campaign",
    verb: "turned",
    fields: ["otto_campaign", "campaign_enabled", "is_campaign_active", "campaign_status"],
    values: {
      TRUE: { label: "on", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "off", tone: AUDIT_LOG_TONE.NEUTRAL },
      ACTIVE: { label: "on", tone: AUDIT_LOG_TONE.POSITIVE },
      INACTIVE: { label: "off", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
};

/**
 * `updatedByType` → who to name when `updatedByName` is absent (system jobs and
 * inbound customer actions have no user record behind them).
 */
export const AUDIT_LOG_ACTOR_FALLBACK = {
  SYSTEM: "System",
  AUTOMATION: "System",
  LEAD: "Customer",
  CUSTOMER: "Customer",
  USER: "User",
};

/**
 * `updated_by_type` → how the message-thread row attributes the change, per
 * Figma node 27039-49871 ("By EngagedAI Support", "Otto (automated)",
 * "By customer"). `byName` true means the actor's name is appended.
 *
 * Distinct from AUDIT_LOG_ACTOR_FALLBACK, which names the actor in the drawer's
 * "{who} • {when}" meta line and so needs no leading preposition.
 */
export const AUDIT_LOG_ATTRIBUTION = {
  USER: { prefix: "By", byName: true },
  SYSTEM: { prefix: "Otto (automated)", byName: false },
  AUTOMATION: { prefix: "Otto (automated)", byName: false },
  OTTO: { prefix: "Otto (automated)", byName: false },
  LEAD: { prefix: "By customer", byName: false },
  CUSTOMER: { prefix: "By customer", byName: false },
  WEB_FORM: { prefix: "Via web form", byName: false },
};
